import { useState, useCallback, useEffect } from "react";
import { useAuth } from "./useAuth";

/**
 * Fetch a single account by ID, with patch support.
 *
 * Calls GET /accounts/{accountId}/ (detail view).
 * Returns the full account object including cs_owner_name, health_score,
 * confidence_level, renewal_forecast, forecast_note, usage_summary, etc.
 */
export const useAccount = (accountId) => {
  const { apiClient } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(!!accountId);
  const [error, setError] = useState(null);

  const fetchAccount = useCallback(async () => {
    if (!accountId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`accounts/${accountId}/`);
      setAccount(response.data);
    } catch (err) {
      console.error("Error fetching account:", err);
      if (err.response?.status === 404) {
        setError("Account not found");
      } else if (err.response?.status !== 401) {
        setError("Failed to fetch account");
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient, accountId]);

  const patchAccount = useCallback(
    async (fields) => {
      if (!accountId) return;
      try {
        const response = await apiClient.patch(
          `accounts/${accountId}/`,
          fields,
        );
        setAccount(response.data);
        return response.data;
      } catch (err) {
        console.error("Error patching account:", err);
        throw err;
      }
    },
    [apiClient, accountId],
  );

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  return {
    account,
    loading,
    error,
    refetch: fetchAccount,
    patchAccount,
  };
};
